import React from 'react';
import type { CompanyDetailsErrors, CompanyDetailsField, GiftVoucherBuyerType } from '@shared/utils/giftVoucherBuyer';
import type { ShopTheme } from '../theme';
import { useI18n } from '../i18n';
import CompanyDetailsFields from './CompanyDetailsFields';
import type { CompanyDetailsForm } from './companyDetailsForm';

/** Plain-language note that the voucher carries no VAT, with a link to the explainer. */
export const NoVatNote: React.FC<{ theme: ShopTheme; text: string; explainerHref: string; className?: string }> = ({
  theme,
  text,
  explainerHref,
  className = '',
}) => {
  const { t } = useI18n();
  return (
    <p className={className} style={{ font: `400 13px/1.55 ${theme.fontFamily}`, color: theme.muted }}>
      {text}{' '}
      <a
        href={explainerHref}
        target="_blank"
        rel="noopener noreferrer"
        className="underline underline-offset-2"
        style={{ color: theme.ink }}
      >
        {t('buyer.how_it_works')}
      </a>
    </p>
  );
};

/**
 * "Buying as: Private person | Company" at the top of the checkout. A company buyer leaves
 * its details so the purchase confirmation (sent for every paid order) carries them.
 */
const BuyerTypeSection: React.FC<{
  theme: ShopTheme;
  buyerType: GiftVoucherBuyerType;
  onBuyerTypeChange: (next: GiftVoucherBuyerType) => void;
  company: CompanyDetailsForm;
  onCompanyChange: (field: CompanyDetailsField, value: string) => void;
  errors: CompanyDetailsErrors;
  venueCountry?: string;
  explainerHref: string;
  disabled?: boolean;
}> = ({ theme, buyerType, onBuyerTypeChange, company, onCompanyChange, errors, venueCountry, explainerHref, disabled }) => {
  const { t } = useI18n();
  const options: GiftVoucherBuyerType[] = ['private', 'company'];

  return (
    <div className="mb-5 pb-5" style={{ borderBottom: `1px solid ${theme.hairlineSoft}` }}>
      <div
        id="buyer-type-label"
        className="mb-2.5"
        style={{
          font: `600 11px/1 ${theme.fontFamily}`,
          letterSpacing: '.16em',
          textTransform: 'uppercase',
          color: theme.label,
        }}
      >
        {t('buyer.buying_as')}
      </div>
      <div
        role="radiogroup"
        aria-labelledby="buyer-type-label"
        className="grid grid-cols-2"
        style={{ border: `1px solid ${theme.inputBorder}`, borderRadius: theme.radius, overflow: 'hidden' }}
      >
        {options.map((option, index) => {
          const selected = buyerType === option;
          return (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              onClick={() => onBuyerTypeChange(option)}
              data-testid={`buyer-type-${option}`}
              className="disabled:cursor-not-allowed"
              style={{
                padding: '12px 14px',
                background: selected ? theme.ink : theme.surface,
                color: selected ? theme.paper : theme.body,
                border: 'none',
                borderLeft: index > 0 ? `1px solid ${theme.inputBorder}` : 'none',
                font: `${selected ? 600 : 500} 15px/1.2 ${theme.fontFamily}`,
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >
              {t(`buyer.${option}`)}
            </button>
          );
        })}
      </div>

      {buyerType === 'company' && (
        <div className="mt-5">
          <NoVatNote theme={theme} text={t('buyer.company_helper')} explainerHref={explainerHref} className="mb-5" />
          <CompanyDetailsFields
            theme={theme}
            values={company}
            onChange={onCompanyChange}
            errors={errors}
            venueCountry={venueCountry}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
};

export default BuyerTypeSection;
